/**
 * aC Men's Hub の技術SEO公式情報アーカイブ API contract（zod schema + 型）。
 *
 * スポットレンタル（エアクロドレス）のサイト刷新にあたり、Google をはじめとする各社が
 * 公式に出している技術SEOの一次情報を蓄積する。画面は持たず、母艦の日次同期から
 * BigQuery へ運ぶ経路だけを提供する。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 技術SEO公式情報アーカイブの取り込み API の型を定義し、母艦の日次同期と API の間で二重定義を排除する。
 * @graph-connects none
 */
import { z } from 'zod';
/**
 * 技術SEO公式情報アーカイブを閲覧する権限。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 技術SEO公式情報アーカイブを読むために必要な権限を表す。
 * @graph-connects none
 */
export const KNOWLEDGE_SEO_PERMISSION = 'knowledge-seo';
/**
 * 技術SEO公式情報アーカイブへ書き込む権限。
 *
 * `KNOWLEDGE_SEO_PERMISSION` とは独立した権限で、閲覧できる相手全員には付かない。
 * このアーカイブはサイト設計の判断根拠を引くための一次情報の写しであり、投入・無効化が
 * できることは改竄ができることと同義なので、閲覧より狭く配る（付与先は日次同期の実行者だけ）。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 技術SEO公式情報アーカイブへ書き込む権限を表す。閲覧とは独立に配る。
 * @graph-connects none
 */
export const KNOWLEDGE_SEO_WRITE_PERMISSION = 'knowledge-seo-write';
/**
 * 出典の信頼区分。
 *
 * SEO の判断で最も危険なのは「Google が言ったこと」と「誰かがそう解釈しただけのこと」を
 * 取り違えること。収録 20,272 件の実測で二次情報（community）が 10,657 件と過半を占めるため、
 * 種類ではなく**根拠としての強さ**で絞り込める軸を別に持つ。
 *
 * Meta公式情報アーカイブ（`knowledge-meta`）にこの軸が無いのは、あちらが全件 Meta 自身の
 * 発信で区別する必要が無いため。技術SEO は発行元と解説者が混在するという性質の違いがある。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 出典の信頼区分。発行元自身の正本か、標準仕様か、実測研究か、第三者の解説かを区別し、根拠の強さで絞り込めるようにする。
 * @graph-connects none
 */
export const KnowledgeSeoTierSchema = z.enum(['official', 'standard', 'research', 'community']);
/**
 * 情報の種類。資料庫の分類をそのまま持つ。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 情報の種類。Google公式・性能・構造化データ・実測研究といった資料庫の分類を表し、読む目的で絞り込めるようにする。
 * @graph-connects none
 */
export const KnowledgeSeoSectionSchema = z.enum([
    '01_Google公式',
    '02_性能とCoreWebVitals',
    '03_構造化データ',
    '04_実測研究',
    '05_他検索エンジンとAI',
    '06_実装',
    '07_日本語',
    '08_専門ブログ',
    '09_規格と一次仕様',
]);
/**
 * 取得元の単位。
 *
 * 種類（`section`）より細かい。`01_Google公式` には検索セントラル・Search Console ヘルプ・
 * Merchant Center ヘルプが同居するため、種類だけでは「検索セントラルの記述だけ」を引けない。
 *
 * **enum にしない。** 取得元の定義は母艦側（資料庫の `_tools/source_tiers.json`）が正本で、
 * ここは受け取るだけ。enum にすると母艦に取得元を1つ足すたびに cortex のデプロイが要り、
 * それまで日次同期が 400 で止まる。値そのものは縛らず形式だけを縛る。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取得元の単位。種類より細かい粒度で絞り込むために使う。値は母艦側の定義が正本なので形式だけを検証する。
 * @graph-connects none
 */
export const KnowledgeSeoCollectionSchema = z
    .string()
    .min(1)
    .max(50)
    .regex(/^[a-z0-9-]+$/);
// ============================================================
// 取り込み（母艦の日次同期 → Hub API → BigQuery）
// ============================================================
/**
 * 本文の受け入れ上限（文字数）。
 *
 * 収録対象 20,272 件の実測は平均 11,165 字で、上限を超えるのは 5 件だけ
 * （メーリングリストのアーカイブ・用語集・タグクラウドで、いずれも一次情報としての価値が低い）。
 * 超える記事は母艦側が送らず警告する。黙って切り詰めると、引用した根拠の後半が
 * 存在しないことに気づけないため。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 技術SEO公式情報の本文受け入れ上限。超過は黙って切り詰めず送らせない。
 * @graph-connects none
 */
export const KNOWLEDGE_SEO_BODY_MAX_CHARS = 300_000;
/**
 * 1 リクエストで送れる記事数の上限。
 *
 * 本文上限 300,000 字は日本語 UTF-8 で最大 900KB になる。20 件で最悪 18MB となり
 * Cloud Run のリクエスト上限（32MB）の内側に収まる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 1リクエストあたりの記事数上限。本文上限と掛けてもCloud Runのリクエスト上限に収まる値。
 * @graph-connects none
 */
export const KNOWLEDGE_SEO_INGEST_BATCH_MAX = 20;
/**
 * 一度に無効化できる記事数の上限。
 *
 * 無効化は「出典から消えた記事を検索対象から外す」操作で、母艦側の取得が一時的に
 * 失敗しただけでも大量に発生しうる。上限を置いて、取りこぼしがそのまま全件無効化に
 * 化けないようにする。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 一度に無効化できる記事数の上限。母艦側の取得失敗が全件無効化へ波及しないための歯止め。
 * @graph-connects none
 */
export const KNOWLEDGE_SEO_DEACTIVATE_MAX = 1000;
/** doc_id / content_hash は SHA-256 の16進表記。 @graph-connects none */
const Sha256HexSchema = z.string().regex(/^[0-9a-f]{64}$/);
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込む記事1件。文字数と同期時刻はサーバ側で決めるため受け取らない。本文と食い違う文字数が入り込む経路を作らないため。
 * @graph-connects none
 */
export const KnowledgeSeoDocumentSchema = z.object({
    docId: Sha256HexSchema,
    sourceUrl: z.url().max(1000),
    sourceTier: KnowledgeSeoTierSchema,
    collection: KnowledgeSeoCollectionSchema,
    section: KnowledgeSeoSectionSchema,
    title: z.string().min(1).max(500),
    body: z.string().min(1).max(KNOWLEDGE_SEO_BODY_MAX_CHARS),
    contentHash: Sha256HexSchema,
    sourceUpdatedAt: z.iso.datetime().nullable(),
    fetchedAt: z.iso.datetime(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込みの入力。1リクエストの件数を制限し、母艦側で分割して送らせる。
 * @graph-connects none
 */
export const KnowledgeSeoIngestInputSchema = z.object({
    documents: z.array(KnowledgeSeoDocumentSchema).min(1).max(KNOWLEDGE_SEO_INGEST_BATCH_MAX),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 取り込みの結果。書き込んだ件数を返し、母艦側のログと突合できるようにする。
 * @graph-connects none
 */
export const KnowledgeSeoIngestResultSchema = z.object({
    upserted: z.number(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 収録済み記事の識別子と内容ハッシュ。母艦がこれと手元を突き合わせ、変わった記事だけを送るために使う。
 * @graph-connects none
 */
export const KnowledgeSeoStateEntrySchema = z.object({
    docId: z.string(),
    contentHash: z.string(),
    isActive: z.boolean(),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 収録済み全件の状態。本文を含まないため2万件でも数MBに収まる。
 * @graph-connects none
 */
export const KnowledgeSeoStateResponseSchema = z.object({
    entries: z.array(KnowledgeSeoStateEntrySchema),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 無効化の入力。物理削除はしない。過去にどの記述を根拠に判断したかを後から辿れなくなるため。
 * @graph-connects none
 */
export const KnowledgeSeoDeactivateInputSchema = z.object({
    docIds: z.array(Sha256HexSchema).min(1).max(KNOWLEDGE_SEO_DEACTIVATE_MAX),
});
/**
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Marketing
 * @graph-business 無効化の結果。実際に状態が変わった件数を返す。
 * @graph-connects none
 */
export const KnowledgeSeoDeactivateResultSchema = z.object({
    deactivated: z.number(),
});
//# sourceMappingURL=knowledge-seo.js.map